/* ------------------- IMPORTS */
import styled from '@emotion/styled'
import { Box, Icons } from 'components'
import { Metadata } from 'components/CreatePost/PreviewComponents/BasicComponents/BasicContentComponents/GenericComponents/Metadata/Metadata'
import { Utm } from 'components/CreatePost/PreviewComponents/BasicComponents/BasicContentComponents/GenericComponents/Utm/Utm'
import { openTemplateModal } from 'components/Modal/ModalManager'
import { IPostVariant } from 'types/PostInterface'
import { AppStore, getActivePost, T } from 'utils'
import { extractAndCompareUrls, getActiveText, updatePost } from 'utils/PostUtils/Post'
import { canShowMetadata, getLinkProperties, onPasteLink } from 'utils/PostUtils/ShortUrl'
import { ExtraTitle } from './ExtraTitle'
import { useEffect, useRef, useState } from 'react'
import { useCreatePostStore } from 'stores/CreatePostStore'

/* ------------------- INTERFACES  */
interface Props {
  post: IPostVariant
}

export const FirstComment = ({ post }: Props) => {
  /* ----------------- VARIABLES  */
  const { variantId, data, info } = post
  const inputRef = useRef<any>(null)
  const showMetadata = canShowMetadata(post)

  const [firstCommentDisabled, setFirstCommentDisabled] = useState(false)
  const [hasDetectedUrl, setHasDetectedUrl] = useState(false)
  const showUtm = getLinkProperties(post)!.length > 0 || hasDetectedUrl

  useEffect(() => {
    if (post.data?.firstComment?.urls && post.data.firstComment.urls.length === 0) setHasDetectedUrl(false)
    else if (getLinkProperties(post)!.length > 0) setHasDetectedUrl(true)
  }, [post])

  return (
    <FirstCommentContainer>
      <ExtraTitle text={T.postPage.extraFirstComment} />

      <TextAreaContainer>
        <ProfileImg src={info.account.picture} />

        <TextArea
          ref={inputRef}
          disabled={firstCommentDisabled}
          style={{ opacity: firstCommentDisabled ? 0.5 : 1 }}
          value={data.firstComment?.text}
          onChange={(e) => {
            const updatedText = e.target.value
            // Utilizza direttamente post.data.firstComment.urls invece di useCreatePostStore.getState()
            const urls = post.data?.firstComment?.urls || []
            // Usa extractAndCompareUrls con il nuovo testo
            const newUrls = extractAndCompareUrls(updatedText, urls)
            // Se troviamo degli URL, settiamo hasDetectedUrl a true
            if (newUrls && newUrls.length > 0) setHasDetectedUrl(true)
            // Aggiorna lo stato con i nuovi URL e testo
            updatePost(post.variantId, {
              firstComment: { ...post?.data?.firstComment, urls: newUrls, text: updatedText },
            })
          }}
          onPaste={async (e) => {
            e.stopPropagation()
            setFirstCommentDisabled(true)
            const activeText = post.data.firstComment?.text ?? ''
            const pastedText = e.clipboardData.getData('text')
            const start = inputRef?.current?.selectionStart
            const end = inputRef?.current?.selectionEnd

            const beforePaste = activeText?.substring(0, start)
            const afterPaste = activeText?.substring(end)

            const processedText = pastedText
            const newValue = beforePaste + processedText + afterPaste

            await onPasteLink({ activePost: post, newText: pastedText, finalText: newValue, firstComment: true })
            setFirstCommentDisabled(false)

            if (post.data?.firstComment?.urls && post.data.firstComment.urls.length > 0) setHasDetectedUrl(true)
          }}
        />

        <TemplatesIconContainer onClick={() => openTemplateModal(variantId)}>
          <Icons.template />
        </TemplatesIconContainer>
      </TextAreaContainer>

      {/* Metadata */}
      {showMetadata && <Metadata post={post} firstComment />}

      {/* UTM */}
      {showUtm && <Utm post={post} firstComment />}
    </FirstCommentContainer>
  )
}

const FirstCommentContainer = styled(Box)`
  width: 100%;
  display: flex;
  gap: 16px;
  flex-direction: column;
`

const TextAreaContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 10px;
  display: flex;
  padding: 16px 24px;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 14px;
  border: 1px dashed ${() => AppStore.theme.o.darkGrey};
  background: ${() => AppStore.theme.o.lightGrey};
`

const ProfileImg = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
`

const TextArea = styled.textarea`
  width: 100%;
  min-height: 40px;
  background-color: transparent;
  border: none;
  border-bottom: 1px solid ${() => AppStore.theme.o.darkGrey};
  color: ${() => AppStore.theme.o.black};
`

const TemplatesIconContainer = styled(Box)`
  cursor: pointer;
  width: 26px;
  height: 26px;

  svg {
    transition: 300ms ease-in-out;
    fill: ${() => AppStore.theme.o.darkGrey};
  }

  &:hover {
    svg {
      fill: ${() => AppStore.theme.o.grey};
    }
  }
`
